import React, { useState, useEffect } from 'react';
import { Table, Button, IconButton, toaster, Card, Tooltip, StarIcon, BanCircleIcon, RemoveIcon, Pane, Badge } from 'evergreen-ui';
import { getCollaborators, removeCollaborator, setAdvancedLevel } from '../services/collaboratorServices';
import EditCollaboratorDialog from './EditCollaboratorDialog';
import AdminDetailsView from './UserDetails/AdminDetailsView';
import useWindowSize from '../utils/useWindowSize';
import '../styles/responsive.css';

const CollaboratorList = ({
    collaborators,
    setCollaborators,
    editingCollaboratorIndex,
    setEditingCollaboratorIndex,
    isEditing,
    isRealDirector,
}) => {

    const [isEditDialogShown, setIsEditDialogShown] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const width = useWindowSize();
    const isMobile = width < 768;
    const isNarrow = width < 1650;

    useEffect(() => {
        const fetchCollaborators = async () => {
            try {
                const collaboratorsData = await getCollaborators();
                setCollaborators(collaboratorsData);
            } catch (error) {
                console.error('Errore nel recupero dei collaboratori:', error);
            }
        };
        fetchCollaborators();
    }, [setCollaborators]);

    const handleCloseEditDialog = () => {
        setIsEditDialogShown(false);
        setEditingCollaboratorIndex(null);
    };

    const handleToggleAdvanced = async (collaborator) => {
        const newValue = !collaborator.is_advanced;
        try {
            const result = await setAdvancedLevel(collaborator.id, newValue);
            if (result.success) {
                toaster.success(newValue ? 'Livello avanzato attivato.' : 'Livello avanzato rimosso.');
                const updatedCollaborators = collaborators.map((c) =>
                    c.id === collaborator.id ? { ...c, is_advanced: newValue } : c
                );
                setCollaborators(updatedCollaborators);
            } else {
                toaster.danger('Errore durante la modifica del livello.');
            }
        } catch (error) {
            console.error('Errore toggle livello avanzato:', error);
            toaster.danger('Errore durante la modifica del livello.');
        }
    };

    const handleRemoveClick = async (collaboratorId) => {
        if (!window.confirm('Sei sicuro di voler rimuovere questo collaboratore?')) return;
        try {
            const result = await removeCollaborator(collaboratorId);
            if (result.success) {
                toaster.success('Collaboratore rimosso con successo.');
                const updatedCollaborators = collaborators.filter((collaborator) => collaborator.id !== collaboratorId);
                setCollaborators(updatedCollaborators);
            } else {
                toaster.danger('Errore durante la rimozione del collaboratore.');
            }
        } catch (error) {
            console.error('Errore durante la rimozione del collaboratore:', error);
            toaster.danger('Errore durante la rimozione del collaboratore.');
        }
    };

    const toggleRow = (index) => {
        setExpandedRow(prev => prev === index ? null : index);
    };

    if (isMobile) {
        return (
            <>
                <div className="responsive-list">
                    {Array.isArray(collaborators) && collaborators.length > 0 ? (
                        collaborators.map((collaborator, index) => (
                            <Card 
                                key={`coll-${collaborator.id || index}`} 
                                className="responsive-card" 
                                style={{ cursor: 'pointer', border: expandedRow === index ? '2px solid #47B881' : 'none' }}
                                onClick={(e) => { 
                                    if(e.target.tagName !== 'BUTTON' && !e.target.closest('button')) toggleRow(index); 
                                }}
                            >
                                <div className="field-row">
                                    <div className="field-label">Email</div>
                                    <div className="field-value">{collaborator.email}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Nome</div>
                                    <div className="field-value">{collaborator.nome}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Cognome</div>
                                    <div className="field-value">{collaborator.cognome}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Ruolo</div>
                                    <div className="field-value">
                                        {collaborator.role}
                                        {!!collaborator.is_advanced && (
                                            <Badge color="blue" marginLeft={8}>Avanzato</Badge>
                                        )}
                                    </div>
                                </div>

                                <div className="actions-row">
                                    {isEditing && (
                                        <>
                                            {isRealDirector && (
                                                <Button
                                                    className="action-btn"
                                                    appearance={collaborator.is_advanced ? 'default' : 'primary'}
                                                    intent={collaborator.is_advanced ? 'none' : 'success'}
                                                    onClick={() => handleToggleAdvanced(collaborator)}
                                                >
                                                    {collaborator.is_advanced ? 'Revoca avanzato' : 'Promuovi avanzato'}
                                                </Button>
                                            )}
                                            <Button className="action-btn" intent="danger" onClick={() => handleRemoveClick(collaborator.id)}>
                                                Rimuovi <RemoveIcon marginLeft={8} />
                                            </Button>
                                        </>
                                    )}
                                </div>

                                {expandedRow === index && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ cursor: 'default', marginTop: 16 }}>
                                        <AdminDetailsView collaborator={collaborator} />
                                    </div>
                                )}
                            </Card>
                        ))
                    ) : (
                        <Card className="responsive-card">Nessun collaboratore disponibile.</Card>
                    )}
                </div>

                <EditCollaboratorDialog
                    isShown={isEditDialogShown}
                    onClose={handleCloseEditDialog}
                    collaborator={editingCollaboratorIndex !== null ? collaborators[editingCollaboratorIndex] : null}
                    fetchCollaborators={async () => {
                        const collaboratorsData = await getCollaborators();
                        setCollaborators(collaboratorsData.filter((collaborator) => collaborator.role !== 'direttore'));
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.TextHeaderCell>Email</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Nome</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Cognome</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Ruolo</Table.TextHeaderCell>
                    {isEditing && <Table.TextHeaderCell>Azioni</Table.TextHeaderCell>}
                </Table.Head>
                <Table.Body>
                    {Array.isArray(collaborators) && collaborators.length > 0 ? (
                        collaborators.map((collaborator, index) => (
                            <React.Fragment key={index}>
                            <Table.Row
                                isSelectable
                                onSelect={() => toggleRow(index)}
                                style={{ backgroundColor: expandedRow === index ? '#f9f9fb' : 'inherit' }}
                            >
                                <Table.TextCell>{collaborator.email}</Table.TextCell>
                                <Table.TextCell>{collaborator.nome}</Table.TextCell>
                                <Table.TextCell>{collaborator.cognome}</Table.TextCell>
                                <Table.TextCell>
                                    {collaborator.role}
                                    {!!collaborator.is_advanced && (
                                        <Badge color="blue" marginLeft={8}>Avanzato</Badge>
                                    )}
                                </Table.TextCell>
                                {isEditing && (
                                    <Table.TextCell style={{ whiteSpace: 'normal' }}>
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center', minWidth: 200, flexWrap: 'wrap' }}>
                                            {isNarrow ? (
                                                <>
                                                    {isRealDirector && (
                                                        <Tooltip content={collaborator.is_advanced ? 'Revoca avanzato' : 'Promuovi avanzato'}>
                                                            <IconButton
                                                                icon={!!collaborator.is_advanced ? BanCircleIcon : StarIcon}
                                                                intent={!!collaborator.is_advanced ? 'danger' : 'success'}
                                                                onClick={() => handleToggleAdvanced(collaborator)}
                                                                aria-label={collaborator.is_advanced ? 'Revoca avanzato' : 'Promuovi avanzato'}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip content="Rimuovi">
                                                        <IconButton icon={RemoveIcon} intent="danger" onClick={() => handleRemoveClick(collaborator.id)} aria-label="Rimuovi" />
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    {isRealDirector && (
                                                        <Button
                                                            appearance={collaborator.is_advanced ? 'default' : 'primary'}
                                                            intent={collaborator.is_advanced ? 'none' : 'success'}
                                                            onClick={() => handleToggleAdvanced(collaborator)}
                                                        >
                                                            {collaborator.is_advanced ? 'Revoca avanzato' : 'Promuovi avanzato'}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        intent="danger"
                                                        onClick={() => handleRemoveClick(collaborator.id)}
                                                    >
                                                        Rimuovi <RemoveIcon marginLeft={8} />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </Table.TextCell>
                                )}
                            </Table.Row>
                            {expandedRow === index && (
                                <Table.Row height="auto" background="tint1">
                                    <Table.Cell padding={0} colSpan={isEditing ? 5 : 4} style={{ borderBottom: '1px solid #E4E7EB' }}>
                                        <Pane paddingX={32} paddingY={16} width="100%">
                                            <AdminDetailsView collaborator={collaborator} />
                                        </Pane>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                            </React.Fragment>
                        ))
                    ) : (
                        <Table.Row>
                            <Table.TextCell>Nessun collaboratore disponibile.</Table.TextCell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            <EditCollaboratorDialog
                isShown={isEditDialogShown}
                onClose={handleCloseEditDialog}
                collaborator={editingCollaboratorIndex !== null ? collaborators[editingCollaboratorIndex] : null}
                fetchCollaborators={async () => {
                    const collaboratorsData = await getCollaborators();
                    setCollaborators(collaboratorsData.filter((collaborator) => collaborator.role !== 'direttore'));
                }}
            />
        </>
    );
};

export default CollaboratorList;